import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  IonPage,
  IonContent,
  IonInput,
  IonButton,
  IonIcon,
  IonModal,
  IonImg,
  IonLabel,
  IonItem,
  IonLoading,
  useIonToast,
  useIonViewWillLeave,
} from "@ionic/react";
import { logoApple, closeOutline } from "ionicons/icons";
import { flushSync } from "react-dom";
import "./login.min.css";
// Assets
import bgImage from "../../assets/images/logo sfondo.png";
import dsLogo from "../../assets/images/ds-logo.svg";
import googleLogo from "./SocialLogos/google.svg";

import { Capacitor } from "@capacitor/core";
import config from "../../settings/config";
import { getFirebaseAuth } from "../../settings/firebaseConfig";
import { onAuthStateChanged } from "firebase/auth";
import { useDispatch } from "react-redux";
import { setAppleRecoverPending } from "../../redux/reducer/userSlice";
import {
  signInWithGoogle,
  signInWithApple,
  consumeAppleProviderEmail,
  consumeAppleRawIdToken,
} from "../../services/auth";
import { useHistory, useLocation } from "react-router";
import {
  cleanStaleAuthData,
  saveCurrentEnvironment,
} from "../../services/authGuard";
import { toErrorText } from "../../services/errorText";
import { getAuthToken, removeAuth } from "../../services/authStorage";
import { trackFunnel } from "../../services/funnelTracker";
import {
  LOGIN_PATH,
  REGISTRATION_PATH,
  completeSignIn,
  isRegistrationSearch,
} from "../../services/signInSession";
import {
  SOCIAL_SIGN_IN_CAPABILITIES,
  clearEmailAttempt,
  readEmailAttempt,
  saveEmailAttempt,
  sendSignInEmail,
  trackEmailAccountOutcome,
  verifySignInCode,
} from "../../services/emailSignIn";

const OTP_LEN = 4;

const REG_TOKEN_KEY = "adoc_rt";
const REG_EXPIRES_KEY = "adoc_rt_expires_at";
const REG_EMAIL_KEY = "adoc_pending_email";

/**
 * The one social button the registration screen shows: Apple on iPhone, Google
 * on Android and the web app (client, 14/09/2026). A development build can
 * preview the other one with `?platform=ios` or `?platform=android`.
 */
const primaryProviderFor = (search) => {
  if (process.env.NODE_ENV !== "production") {
    const forced = new URLSearchParams(search || "").get("platform");
    if (forced === "ios") return "apple";
    if (forced === "android" || forced === "web") return "google";
  }
  return Capacitor.getPlatform() === "ios" ? "apple" : "google";
};

/** "google" | "apple" for the Firebase user a web redirect brings back. */
const providerOfUser = (user) => {
  const id = String(user?.providerData?.[0]?.providerId || "");
  if (id.startsWith("apple")) return "apple";
  if (id.startsWith("google")) return "google";
  return null;
};

const Login = () => {
  const dispatch = useDispatch();
  const [toast] = useIonToast();
  const history = useHistory();
  const location = useLocation();

  // One screen, two modes: the intro's "Salta" and "INIZIA GRATIS" open the
  // registration, "Hai già un account? Accedi" opens the login.
  const isRegistration = isRegistrationSearch(location.search);
  const primaryProvider = useMemo(
    () => primaryProviderFor(location.search),
    [location.search],
  );

  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  // The registration leads with the device's social button and opens the email
  // form from "Continua con email"; the login shows the form straight away.
  const [emailFormOpen, setEmailFormOpen] = useState(false);

  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otpDigits, setOtpDigits] = useState(Array(OTP_LEN).fill(""));
  const otpRefs = useRef([]); // native <input> refs
  // The button in the email is the main path: the code boxes stay hidden, and
  // the keyboard closed, until the member asks for them (client, 14/09/2026).
  const [codeEntryOpen, setCodeEntryOpen] = useState(false);

  // Where the last email went, whether it carries the button, and when
  // "Reinvia email" becomes available.
  const [sentTo, setSentTo] = useState("");
  const [linkSent, setLinkSent] = useState(false);
  const [resendAt, setResendAt] = useState(0);
  const [now, setNow] = useState(() => Date.now());

  const emailInputRef = useRef(null);
  const isAuthCheckInProgress = useRef(false);

  const emailTrimmed = useMemo(() => (email || "").trim(), [email]);

  const isEmailValid = useMemo(() => {
    if (!emailTrimmed) return false;
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(emailTrimmed);
  }, [emailTrimmed]);

  const otpValue = useMemo(() => otpDigits.join(""), [otpDigits]);
  const resendIn = Math.max(0, Math.ceil((resendAt - now) / 1000));

  const setError = (message) => {
    toast({
      // Coerce objects/Errors to a readable string so a non-string backend
      // error never surfaces as "[object Object]".
      message: toErrorText(message, "Ops! C'è stato un errore. Riprova più tardi."),
      duration: 5000,
    });
  };

  useEffect(() => {
    // Clean stale/expired tokens and env mismatches before deciding
    if (cleanStaleAuthData()) return; // tokens were stale — stay on Login

    const hasBearer = !!getAuthToken();
    const regToken = localStorage.getItem(REG_TOKEN_KEY);
    if (regToken && !hasBearer) {
      // Check if registration token has expired (e.g. Android WebView
      // can persist localStorage across app uninstall/reinstall)
      const expiresAt = localStorage.getItem(REG_EXPIRES_KEY);
      if (expiresAt && new Date(expiresAt) < new Date()) {
        localStorage.removeItem(REG_TOKEN_KEY);
        localStorage.removeItem(REG_EXPIRES_KEY);
        localStorage.removeItem(REG_EMAIL_KEY);
        return; // Stay on Login page
      }
      history.replace("/impostazioni-iniziali/1");
      return;
    }

    // Back from the mail app without the button bringing the member here: the
    // email is on its way, so show where to type its code.
    const attempt = hasBearer ? null : readEmailAttempt();
    if (attempt?.email) {
      setEmail(attempt.email);
      setSentTo(attempt.email);
      setLinkSent(true);
      setEmailFormOpen(true);
      setShowOtpModal(true);
    }
  }, [history]);

  // Counts down "Reinvia email tra N s" while the modal is open.
  useEffect(() => {
    if (!showOtpModal) return undefined;
    const left = resendAt - Date.now();
    if (left <= 0) return undefined;
    const timer = setTimeout(() => setNow(Date.now()), Math.min(1000, left));
    return () => clearTimeout(timer);
  }, [showOtpModal, resendAt, now]);

  // The email's button opens another page: don't leave the modal over it.
  useIonViewWillLeave(() => setShowOtpModal(false));

  const getFirebaseIdToken = async (user) => {
    if (!user) return null;

    // Preferred (stable across platforms)
    if (typeof user.getIdToken === "function") {
      return await user.getIdToken(true);
    }

    // Fallbacks (some builds expose these)
    return user?.stsTokenManager?.accessToken || user?.accessToken || null;
  };

  const authCheck = async (user, provider = providerOfUser(user)) => {
    if (isAuthCheckInProgress.current) return;
    isAuthCheckInProgress.current = true;

    try {
      const idToken = await getFirebaseIdToken(user);
      if (!idToken) {
        throw new Error("Token non disponibile (Firebase ID token mancante).");
      }

      // If Apple just shared a real email, forward it: Firebase's user.email can
      // be a stale relay, so the backend adopts this as the account's email.
      // Also forward the raw Apple identity token so the backend can decode/verify
      // the email claim itself (authoritative) and report what Apple returned.
      const providerEmail = consumeAppleProviderEmail();
      const appleIdToken = consumeAppleRawIdToken();
      // ✅ send ID token. This screen shows the Terms line, so the backend also
      // records the required agreements.
      const body = { token: idToken, capabilities: SOCIAL_SIGN_IN_CAPABILITIES };
      if (providerEmail) body.provider_email = providerEmail;
      if (appleIdToken) body.apple_id_token = appleIdToken;

      const response = await fetch(
        `${config.env[config.env.mode].url}/api/social-login`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        },
      );

      if (!response.ok) {
        const body = await response.text().catch(() => "");
        console.error(
          "social-login failed:",
          response.status,
          response.statusText,
          body,
        );
        throw new Error("Ops! C'è stato un errore. Err: 1001");
      }

      const data = await response.json();
      let token;
      try {
        token = window.atob(data.message);
      } catch (e) {
        console.error("social-login: invalid base64 in response.message", data);
        throw new Error("Ops! C'è stato un errore. Err: 1002");
      }

      const { incomplete, nextPath } = await completeSignIn({ token, dispatch });

      // Google and Apple vouch for the address themselves, so there's no "email
      // verified" step: a new account is started and completed in one go.
      const method = provider || "social";
      if (provider) trackFunnel("auth_completed", { provider });
      if (data?.is_new_account === true) {
        trackFunnel("registration_started", { method });
        trackFunnel("registration_completed", { method });
      } else if (incomplete) {
        // An account that never finished onboarding is still a registration.
        trackFunnel("registration_started", { method });
      } else {
        trackFunnel("login_existing", { method });
      }

      // Apple "Hide My Email": a relay address is a temporary, non-deliverable
      // identity, so offer the GLOBAL recover interstitial (<AppleRecoverGate/>,
      // mounted at the app root) on EVERY relay sign-in — even when the backend
      // already matched an existing account (relay stays stable per Apple ID, so
      // it keeps auto-matching). We only stop once the account has a VERIFIED real
      // email (recovered or captured), since that's a real identity. Gating on
      // onboarding-incomplete was too weak: a relay account onboarded in a prior
      // session was silently auto-logged-in without ever being offered recovery.
      // The gate can't live on /login — RouteAuth redirects authenticated users
      // off it before it could show — hence the app-root mount over /home.
      dispatch(
        setAppleRecoverPending(
          !!(
            data?.email_is_private_relay && !data?.has_verified_real_email
          ),
        ),
      );

      history.replace(nextPath);
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setLoading(false); // ✅ always stop loader
      isAuthCheckInProgress.current = false;
    }
  };

  // Web-only: Firebase uses signInWithRedirect, so we need onAuthStateChanged
  // to pick up the user after the browser comes back. On native the signIn
  // functions return the UserCredential directly; handleSocialSubmit calls
  // authCheck immediately with no listener needed.
  useEffect(() => {
    if (Capacitor.isNativePlatform()) return;

    const auth = getFirebaseAuth();
    // Capture the uid present at mount so a pre-existing Firebase session
    // doesn't silently re-trigger authCheck.
    const initialUid = auth.currentUser?.uid || null;

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) return;
      if (user.uid === initialUid) return; // stale session — require a fresh button tap
      if (getAuthToken()) return; // already exchanged

      setLoading(true);
      await authCheck(user);
    });
    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isUserCancel = (error) => {
    const code = String(error?.code || "").toLowerCase();
    const msg = String(error?.message || "").toLowerCase();
    // @capacitor-firebase/authentication surfaces native cancel as these:
    return (
      code.includes("cancel") ||
      code === "1001" || // iOS ASAuthorizationError.canceled surfaced as a code
      msg.includes("cancel") ||
      msg.includes("canceled") ||
      msg.includes("the user canceled") ||
      // iOS Apple sign-in "cancel" arrives ONLY as this AuthorizationError, with
      // no "cancel" word — e.g. "...AuthenticationServices.AuthorizationError
      // error 1001." — so match the domain + 1001 (canceled) explicitly.
      (msg.includes("authorizationerror") && msg.includes("1001")) ||
      (msg.includes("authenticationservices") && msg.includes("1001")) ||
      msg.includes("12501") // Android GoogleSignIn user-cancel
    );
  };

  const isNoGoogleCredentialError = (error) => {
    const msg = String(error?.message || "").toLowerCase();
    return (
      msg.includes("no credentials available") ||
      msg.includes("nocredentialexception")
    );
  };

  // Microsoft was removed on 14/09/2026: nobody had signed up with it since the
  // funnel started, and those accounts can use "Continua con email".
  const handleSocialSubmit = async (providerName) => {
    setLoading(true);
    trackFunnel("auth_started", { provider: providerName });
    try {
      const result =
        providerName === "apple"
          ? await signInWithApple()
          : await signInWithGoogle();

      // On web the signInWithRedirect call above navigates away; nothing to do.
      // On native we get a UserCredential back and call authCheck directly.
      if (result?.user) {
        await authCheck(result.user, providerName);
      }
    } catch (error) {
      setLoading(false);
      if (isUserCancel(error)) {
        // Silent — user closed the provider sheet.
        return;
      }
      if (providerName === "google" && isNoGoogleCredentialError(error)) {
        setError(
          "Nessun account Google trovato sul dispositivo. Aggiungi un account Google nelle impostazioni e riprova.",
        );
        return;
      }
      // Keep the raw native error in the console for debugging, but never surface
      // technical details (SDK codes / Apple AuthorizationError strings) to users.
      console.error("Social sign-in failed:", providerName, error);
      setError("Accesso non riuscito. Riprova più tardi.");
    }
  };

  // ----------------------------
  // EMAIL: button + code
  // ----------------------------

  // Sends the sign-in email (a button and the code, or the code only on an older
  // backend) and remembers what the "check your email" modal needs.
  const requestSignInEmail = async (address) => {
    const { ok, data } = await sendSignInEmail(address);
    if (!ok) throw new Error(toErrorText(data, "Errore invio email"));

    if (data?.attempt_secret) {
      saveEmailAttempt({
        email: address,
        secret: data.attempt_secret,
        expiresAt: data.expires_at,
        mode: isRegistration ? "registration" : "login",
      });
    } else {
      clearEmailAttempt();
    }

    setSentTo(address);
    setLinkSent(!!data?.attempt_secret);
    setResendAt(Date.now() + (Number(data?.resend_after) || 60) * 1000);
    setNow(Date.now());
  };

  const handleEmailContinue = async () => {
    if (!isEmailValid) return;

    setLoading(true);

    // clear previous pending session
    localStorage.removeItem(REG_TOKEN_KEY);
    localStorage.removeItem(REG_EXPIRES_KEY);
    localStorage.removeItem(REG_EMAIL_KEY);

    try {
      await requestSignInEmail(emailTrimmed);

      // The funnel's "registration started" for email: the member has committed
      // an address and asked for the email. Fired on success only.
      trackFunnel("registration_started", { method: "email" });

      setOtpDigits(Array(OTP_LEN).fill(""));
      setCodeEntryOpen(false);
      setShowOtpModal(true);
    } catch (e) {
      setError(e?.message || "Errore invio email");
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (loading || resendIn > 0 || !sentTo) return;

    setLoading(true);
    try {
      await requestSignInEmail(sentTo);
      trackFunnel("email_resent");
      setOtpDigits(Array(OTP_LEN).fill(""));
      toast({ message: "Ti abbiamo inviato una nuova email.", duration: 2500 });
    } catch (e) {
      setError(e?.message || "Errore invio email");
    } finally {
      setLoading(false);
    }
  };

  const handleChangeEmail = () => {
    trackFunnel("email_changed");
    clearEmailAttempt();
    setOtpDigits(Array(OTP_LEN).fill(""));
    setCodeEntryOpen(false);
    setShowOtpModal(false);
    setEmailFormOpen(true);
    setTimeout(() => emailInputRef.current?.setFocus?.(), 300);
  };

  const checkOTP = async (otp) => {
    setLoading(true);
    const address = sentTo || emailTrimmed;

    try {
      const { ok, status, data } = await verifySignInCode(address, otp);

      // The backend's reasons are in English ("Invalid OTP code.", "OTP not found
      // for this email."), and the code is now checked the moment the last digit
      // goes in, so say in Italian what went wrong and what to do.
      if (!ok) {
        throw new Error(
          status === 429
            ? "Troppi tentativi. Riprova tra qualche minuto."
            : "Codice non valido o scaduto. Controlla l'email e riprova.",
        );
      }

      // The code IS the email verification in this app, for new and existing
      // accounts alike.
      trackFunnel("email_verified", { method: "code" });

      if (data?.status === "registered") {
        trackEmailAccountOutcome(data);
        clearEmailAttempt();
        setShowOtpModal(false);

        const { nextPath } = await completeSignIn({ token: data.token, dispatch });
        setLoading(false);
        history.replace(nextPath);
        return;
      }

      if (data?.status === "pending") {
        // An older backend: the account is created during onboarding.
        clearEmailAttempt();
        removeAuth("adoc_t");
        removeAuth("adoc_p");

        localStorage.setItem(REG_TOKEN_KEY, data.token);
        if (data.expires_at)
          localStorage.setItem(REG_EXPIRES_KEY, data.expires_at);
        localStorage.setItem(REG_EMAIL_KEY, address);
        saveCurrentEnvironment();

        setShowOtpModal(false);
        setLoading(false);
        history.replace("/impostazioni-iniziali/1");
        return;
      }

      throw new Error("Risposta inattesa dal server");
    } catch (e) {
      setError(
        e instanceof TypeError
          ? "Connessione non riuscita. Riprova."
          : e?.message || "Verifica non riuscita. Riprova.",
      );
      // The code is checked as soon as the boxes are full, so start again from
      // an empty first box (once the loading overlay has handed focus back).
      setOtpDigits(Array(OTP_LEN).fill(""));
      setTimeout(() => focusOtp(0), 400);
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = () => {
    if (otpValue.length !== OTP_LEN) return;
    checkOTP(otpValue);
  };

  // When the last OTP box blurs, Enter may no longer hit input handlers.
  // Keep Enter/NumpadEnter working while OTP modal is open.
  useEffect(() => {
    if (!showOtpModal) return;

    const handleGlobalEnter = (e) => {
      const key = e.key;
      if (key !== "Enter" && key !== "NumpadEnter") return;
      if (otpValue.length !== OTP_LEN || loading) return;
      e.preventDefault();
      verifyOtp();
    };

    window.addEventListener("keydown", handleGlobalEnter);
    return () => window.removeEventListener("keydown", handleGlobalEnter);
  }, [showOtpModal, otpValue, loading]);

  // ----------------------------
  // OTP input behavior (native inputs)
  // ----------------------------
  const focusOtp = (i) => {
    const el = otpRefs.current[i];
    if (!el) return;
    el.focus();
    el.select && el.select();
  };

  // Shows the code boxes and focuses the first one within the same tap, which
  // is what lets iOS open the keyboard for it.
  const openCodeEntry = () => {
    flushSync(() => setCodeEntryOpen(true));
    focusOtp(0);
  };

  const submitWhenComplete = (code) => {
    if (code.length !== OTP_LEN || loading) return;
    checkOTP(code);
  };

  const setDigit = (index, rawValue) => {
    const digit = String(rawValue || "")
      .replace(/\D/g, "")
      .slice(-1);

    const next = [...otpDigits];
    next[index] = digit;
    setOtpDigits(next);

    if (!digit) return;

    // All four boxes filled, in whatever order: check the code, no OK button.
    const code = next.join("");
    if (code.length === OTP_LEN) {
      submitWhenComplete(code);
      return;
    }

    if (index < OTP_LEN - 1) {
      requestAnimationFrame(() => focusOtp(index + 1));
    }
  };

  const handleOtpChange = (index, e) => {
    setDigit(index, e.target.value);
  };

  const handleOtpKeyDown = (index, e) => {
    const key = e.key;

    if (key === "Enter" || key === "NumpadEnter") {
      e.preventDefault();
      if (otpValue.length === OTP_LEN && !loading) {
        verifyOtp();
      }
      return;
    }

    if (key === "Backspace") {
      // allow controlled behavior
      e.preventDefault();

      // if current has value => clear current
      if (otpDigits[index]) {
        setOtpDigits((prev) => {
          const next = [...prev];
          next[index] = "";
          return next;
        });
        requestAnimationFrame(() => focusOtp(index));
        return;
      }

      // if current empty => move left and clear left
      if (index > 0) {
        setOtpDigits((prev) => {
          const next = [...prev];
          next[index - 1] = "";
          return next;
        });
        requestAnimationFrame(() => focusOtp(index - 1));
      }
      return;
    }

    // arrow nav
    if (key === "ArrowLeft" && index > 0) {
      e.preventDefault();
      requestAnimationFrame(() => focusOtp(index - 1));
    }
    if (key === "ArrowRight" && index < OTP_LEN - 1) {
      e.preventDefault();
      requestAnimationFrame(() => focusOtp(index + 1));
    }
  };

  const handleOtpPaste = (e) => {
    const text = (e.clipboardData.getData("text") || "")
      .replace(/\D/g, "")
      .slice(0, OTP_LEN);

    if (!text) return;

    e.preventDefault();

    const arr = Array(OTP_LEN).fill("");
    for (let i = 0; i < text.length; i++) arr[i] = text[i];

    setOtpDigits(arr);

    if (text.length >= OTP_LEN) {
      submitWhenComplete(arr.join(""));
      return;
    }
    requestAnimationFrame(() => focusOtp(text.length));
  };

  const switchMode = (toRegistration) => {
    setEmailFormOpen(false);
    history.replace(toRegistration ? REGISTRATION_PATH : LOGIN_PATH);
  };

  const openEmailForm = () => {
    setEmailFormOpen(true);
    setTimeout(() => emailInputRef.current?.setFocus?.(), 150);
  };

  const renderSocialButton = (provider) => (
    <IonButton
      className="ds-login__socialBtn"
      expand="block"
      fill="outline"
      onClick={() => handleSocialSubmit(provider)}
      disabled={loading}
    >
      <span className="ds-login__socialContent">
        <span className="ds-login__socialIconSlot">
          {provider === "apple" ? (
            <IonIcon
              className="ds-login__socialIcon ds-login__appleIcon"
              icon={logoApple}
            />
          ) : (
            <IonImg className="ds-login__socialIcon" src={googleLogo} alt="" />
          )}
        </span>
        <span className="ds-login__socialText">
          {provider === "apple" ? "Continua con Apple" : "Continua con Google"}
        </span>
      </span>
    </IonButton>
  );

  const renderEmailForm = () => (
    <>
      <IonLabel
        onClick={() => emailInputRef.current?.setFocus()}
        className="ds-login__label"
      >
        La tua e-mail
      </IonLabel>

      <IonItem className="ds-login__item" lines="none">
        <IonInput
          ref={emailInputRef}
          className="ds-login__input"
          type="email"
          inputMode="email"
          autocomplete="email"
          enterkeyhint="done"
          value={email}
          placeholder=""
          onIonInput={(e) => setEmail(e.detail.value ?? "")}
        />
      </IonItem>

      <IonButton
        className="ds-login__primaryBtn"
        expand="block"
        color="primary"
        onClick={handleEmailContinue}
        disabled={!isEmailValid || loading}
      >
        Continua
      </IonButton>
    </>
  );

  const divider = (
    <div className="ds-login__divider" role="separator" aria-label="oppure">
      <span>oppure</span>
    </div>
  );

  return (
    <IonPage className="ds-login">
      <IonContent fullscreen className="ds-login__content">
        <div
          className={`ds-login__bg${isRegistration ? " ds-login__bg--centered" : ""}`}
          style={{ backgroundImage: `url('${bgImage}')` }}
          aria-hidden="true"
        />

        {/* «Crea il tuo account»: content centred on the screen (client, 05/11/2026). */}
        <div
          className={`ds-login__wrap${isRegistration ? " ds-login__wrap--centered" : ""}`}
        >
          <div className="ds-login__logo">
            <IonImg src={dsLogo} alt="Dieta Social" />
          </div>

          <div className="ds-login__title">
            {isRegistration ? "Crea il tuo account" : "Accedi al tuo account"}
          </div>
          {isRegistration && (
            <div className="ds-login__subtitle">Inizia gratis con Dieta Social</div>
          )}

          <div className="ds-login__form">
            {isRegistration ? (
              <>
                {renderSocialButton(primaryProvider)}
                {divider}
                {emailFormOpen ? (
                  renderEmailForm()
                ) : (
                  <button
                    type="button"
                    className="ds-login__emailToggle"
                    onClick={openEmailForm}
                  >
                    Continua con email
                  </button>
                )}
              </>
            ) : (
              <>
                {/* Every method on every device: a member may have signed up
                    with Apple and now be on Android. */}
                <div className="ds-login__socialStack">
                  {renderSocialButton("google")}
                  {renderSocialButton("apple")}
                </div>
                {divider}
                {renderEmailForm()}
              </>
            )}

            <p className="ds-login__terms">
              {"Continuando accetti i "}
              <button
                type="button"
                className="ds-login__termsLink"
                onClick={() => history.push("/legal/terms")}
              >
                Termini e Condizioni
              </button>
              {" e dichiari di aver preso visione dell'"}
              <button
                type="button"
                className="ds-login__termsLink"
                onClick={() => history.push("/legal/privacy-policy")}
              >
                Informativa Privacy
              </button>
              .
            </p>

            <div className="ds-login__switch">
              {isRegistration ? "Hai già un account? " : "Non hai un account? "}
              <button
                type="button"
                className="ds-login__switchLink"
                onClick={() => switchMode(!isRegistration)}
              >
                {isRegistration ? "Accedi" : "Registrati"}
              </button>
            </div>
          </div>
        </div>

        {/* "Controlla la tua email": the button in the email, or its code */}
        <IonModal
          isOpen={showOtpModal}
          className="otp-modal"
          onDidDismiss={() => setShowOtpModal(false)}
        >
          <div className="otp-card" onPaste={handleOtpPaste}>
            <div className="otp-top">
              <div className="otp-title">Controlla la tua email</div>

              <button
                type="button"
                className="otp-close"
                aria-label="Chiudi"
                onClick={() => setShowOtpModal(false)}
              >
                <IonIcon icon={closeOutline} />
              </button>
            </div>

            <div className="otp-text">
              {linkSent ? (
                <>
                  {"Ti abbiamo inviato un'email a "}
                  <strong>{sentTo}</strong>
                  {". Tocca il pulsante che trovi nell'email per continuare."}
                </>
              ) : (
                <>
                  {"Ti abbiamo inviato un codice a "}
                  <strong>{sentTo}</strong>.
                </>
              )}
            </div>

            <div className="otp-subtitle">
              (Se non la trovi, controlla nella cartella <br />
              Spam o Posta Indesiderata)
            </div>

            {linkSent && !codeEntryOpen ? (
              <div className="otp-fallback">
                {"Non riesci a usare il pulsante nell'email? "}
                <button
                  type="button"
                  className="otp-link"
                  onClick={openCodeEntry}
                  disabled={loading}
                >
                  Inserisci il codice
                </button>
              </div>
            ) : (
              <>
                <div className="otp-codeLabel">
                  {linkSent
                    ? "Inserisci il codice che trovi nell'email:"
                    : "Inserisci il codice:"}
                </div>

                <div className="otp-boxes">
                  {otpDigits.map((d, i) => (
                    <input
                      key={i}
                      ref={(el) => (otpRefs.current[i] = el)}
                      className="otp-box"
                      value={d}
                      inputMode="numeric"
                      pattern="[0-9]*"
                      autoComplete="one-time-code"
                      maxLength={1}
                      onChange={(e) => handleOtpChange(i, e)}
                      onKeyDown={(e) => handleOtpKeyDown(i, e)}
                    />
                  ))}
                </div>
              </>
            )}

            <div className="otp-links">
              <button
                type="button"
                className="otp-link otp-link--muted"
                onClick={handleChangeEmail}
                disabled={loading}
              >
                Modifica email
              </button>

              <button
                type="button"
                className="otp-resend"
                onClick={handleResend}
                disabled={loading || resendIn > 0}
              >
                {resendIn > 0 ? `Reinvia email tra ${resendIn} s` : "Reinvia email"}
              </button>
            </div>
          </div>
        </IonModal>

        <IonLoading isOpen={loading} message={"Attendere prego..."} />
      </IonContent>
    </IonPage>
  );
};

export default Login;
